import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../../api";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../../helpers/toastMessage";

export interface DeclineOrderPayload {
  orderIds: (string | number)[];
}

export interface DeclineOrderDetail {
  id: string | number;
  patientFirstName?: string;
  patientLastName?: string;
}

export type DeclineOrderVariables =
  | string
  | number
  | (string | number)[]
  | {
      orderIds: (string | number)[];
      orderDetails?: DeclineOrderDetail[];
    };

function useDeclineOrder() {
  const { authState } = useOktaAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["declineOrder"],
    mutationFn: (variables: DeclineOrderVariables) => {
      const isPayload =
        typeof variables === "object" &&
        !Array.isArray(variables) &&
        "orderIds" in variables;
      const ids = isPayload
        ? variables.orderIds
        : Array.isArray(variables)
          ? variables
          : [variables];
      const payload: DeclineOrderPayload = { orderIds: ids };
      return axiosInstance
        .put("v1/orders/new-flow/decline-orders", payload, {
          headers: {
            Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
          },
        })
        .then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      const isPayload =
        typeof variables === "object" &&
        !Array.isArray(variables) &&
        "orderIds" in variables;
      const orderDetails = isPayload ? variables.orderDetails : undefined;
      const ids = isPayload
        ? variables.orderIds
        : Array.isArray(variables)
          ? variables
          : [variables];

      if (orderDetails && orderDetails.length > 0) {
        orderDetails.forEach((order) => {
          const firstName = order.patientFirstName ?? "";
          const lastName = order.patientLastName ?? "";
          const name = [firstName, lastName].filter(Boolean).join(" ").trim();
          const displayName = name || "this patient";
          toastMessages.success(
            `Esopredict order for ${displayName} has been successfully declined`
          );
        });
      } else {
        ids.forEach(() => {
          toastMessages.success(
            "Esopredict order has been successfully declined"
          );
        });
      }
      queryClient.invalidateQueries({ queryKey: ["testOrdersList"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardStatus"] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to decline order";
      toastMessages.error(message);
    },
  });
}

export default useDeclineOrder;
